<template>
  <div v-if="isEmptyToolData" class="page-title">Tasks and Tools</div>
  <div class="tools-page">
    <!-- Flex container for two-column layout -->
    <div class="tools-container">
      <!-- Left column for tasks, hidden if toolData is specified -->
      <div v-if="isEmptyToolData" class="tasks-column">
        <div v-for="task in tasks" :key="task.id" class="task-item">
          <div class="task-name">{{ task.name }}</div>
          <!-- Conditionally render input field if hasArg is true -->
          <input
            v-if="task.hasArg"
            :placeholder="task.prompt"
            v-model="task.arg"
            class="task-arg-input"
            type="text"
            :title="task.tips"
          />
          <div v-else class="empty-space">&nbsp;</div>
          <button @click="runTask(task)">Go</button>
          <button @click="stopTask(task)">Stop</button>
        </div>
      </div>

      <!-- Right column for output, shrink if toolData is specified -->
      <div
        :class="['output-column', { shrunk: !isEmptyToolData }]"
        ref="outputDisplay"
      >
        <div class="button-row">
          <button @click="clearOutput">Clear</button>
        </div>
        <div class="output-display">
          <div v-html="output"></div>
        </div>

        <!-- Input box for sending input to the script -->
        <div class="input-pane">
          <input
            type="text"
            v-model="userInput"
            v-if="!hideControls"
            placeholder="Type input for the script and press Enter..."
            @keyup.enter="sendInputToScript"
          />
          <button v-if="!hideControls" @click="sendInputToScript">Send</button>
        </div>
      </div>
    </div>

    <!-- Close button if canClose is true -->
    <button
      v-if="canClose"
      style="margin-top: 10px; margin-left: -54px"
      @click="closeComponent"
    >
      Close
    </button>
  </div>
</template>

<script>
import { io } from 'socket.io-client';
import { marked } from 'marked';

export default {
  props: {
    toolData: {
      type: Object,
      default: () => ({}), // Ensure it returns a new object each time
    },
    canClose: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'], // Declare that ToolsPage emits the "close" event
  data() {
    return {
      hideControls: false,
      socket: null,
      output: '',
      userInput: '', // Input from the user to send to the script
      tasks: [
        {
          id: 0,
          name: 'Automatch',
          script: 'AutoMatchQBtoBank.js',
          hasArg: true,
          arg: '',
          tips: 'options: \n--all (otherwise only unmatched)',
        },
        {
          id: 2,
          name: 'Process Receipts',
          script: 'processUnreadReceipts.js',
          hasArg: true,
          arg: '',
          tips: 'options: --debug',
        },
        {
          id: 5,
          name: 'import DEX deposits',
          script: 'updateDexDepositSplits.js',
          hasArg: false,
          arg: '',
        },
        {
          id: 6,
          name: 'count lines',
          script: '../node-scripts/countlines.js',
          hasArg: false,
          arg: '',
        },
        {
          id: 7,
          name: 'Reconcile DEX to Bank',
          script: 'reconcileDex.js',
          hasArg: false,
          arg: '',
        },
        {
          id: 9,
          name: 'load QuickBooks data',
          script: 'python ../py-scripts/load_qb_data.py',
          hasArg: true,
          arg: '',
          tips: 'options: \n--debug',
        },
        {
          id: 1,
          name: 'Splits processing',
          script: 'QBSplitsThread.js',
          hasArg: true,
          arg: '',
          tips: 'options: \n--all (otherwise, do only new splits)',
        },
        {
          id: 10,
          name: 'Manage Users',
          script: 'python ../py-scripts/manage_users.py',
          hasArg: false,
          arg: '',
        },
        {
          id: 11,
          name: 'IMAP import leases',
          script: 'imapImportPDFs.js',
          hasArg: true,
          arg: '--leases',
          tips: 'options: \n--leases, \n--integrityCheck,\n--debug, \n--imapDebug',
        },
        {
          id: 12,
          name: 'IMAP import documents',
          script: 'imapImportPDFs.js',
          hasArg: true,
          arg: '',
          tips: 'options: \n--leases, \n--integrityCheck,\n--debug, \n--imapDebug',
        },
        {
          id: 14,
          name: 'Clean email bounces',
          script: 'resolveBouncedEmails.js',
          hasArg: false,
          arg: '',
          tips: '',
        },
        // Add more tasks as needed
      ],
      highlightTerms: ['fundingRefId', 'Credit', 'Debit', 'Amount', 'ACH-\\S+'], // Array of terms to highlight
    };
  },

  created() {
    // Check if toolData is empty or not provided
    if (
      Object.keys(this.toolData).length === 0 &&
      this.toolData.constructor === Object
    ) {
      document.title = 'Tools';
    } else {
      this.hideControls = true;
    }
  },

  watch: {
    toolData: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.toolId) {
          this.handleRunTool(newVal.toolId, newVal.options);
        }
      },
    },
  },
  computed: {
    isEmptyToolData() {
      // Check if the object has any properties
      return Object.keys(this.toolData).length === 0;
    },
  },
  methods: {
    handleRunTool(toolId, options) {
      const task = this.tasks.find((t) => t.id === toolId);
      if (task) {
        // Add the options to the task arguments
        task.arg = options || '';
        this.runTask(task);
      }
    },

    waitForSocketConnection() {
      return new Promise((resolve) => {
        if (this.socket && this.socket.connected) {
          resolve();
        } else {
          const checkConnection = setInterval(() => {
            if (this.socket && this.socket.connected) {
              clearInterval(checkConnection);
              resolve();
            }
          }, 500); // Check every 500ms
        }
      });
    },

    async runTask(task) {
      // console.log('Running task:', task.name);

      // Wait until the WebSocket is connected
      await this.waitForSocketConnection();

      this.output = 'options: '; // Clear the output before running a new task
      let script = task.script;

      if (task.hasArg && task.arg) {
        script += ` ${task.arg}`;
        this.output += task.arg + '\n';
      }

      // console.log('Sending script to server:', script);
      this.socket.emit('runScript', script);
    },

    stopTask(task) {
      let script = task.script;

      if (task.hasArg && task.arg) {
        script += ` ${task.arg}`;
      }

      if (this.socket && this.socket.connected) {
        this.socket.emit('stopScript', script);
      } else {
        this.output += '\nSocket not connected. Please check your connection.';
      }
    },
    sendInputToScript() {
      if (this.userInput.trim() !== '') {
        const inputWithNewline = this.userInput + '\n';
        this.socket.emit('sendInput', inputWithNewline);
        this.output += inputWithNewline;
        this.userInput = '';
      }
    },
    closeComponent() {
      // Emit an event to close the component when incorporated into another
      this.$emit('close');
    },
    preprocessHighlight(content) {
      // Iterate over the array of highlight terms and wrap each occurrence in a span
      this.highlightTerms.forEach((term) => {
        const regex = new RegExp(`(${term})`, 'gi'); // Create a regex to match the term, case insensitive
        content = content.replace(
          regex,
          '<span class="highlight-term">$1</span>',
        ); // Wrap the term with a span
      });
      return content;
    },
    clearOutput() {
      this.output = '';
    },

    getAuthToken() {
      const token = localStorage.getItem('token');
      return token;
    },

    connectSocket() {
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;

        // Check if socketUrl is defined
        if (!socketUrl) {
          console.error('Socket URL is undefined.');
          this.output += '\nFailed to connect: Socket URL is not set.';
          return; // Exit the function early
        }

        // console.log('socketURL:', socketUrl);

        const token = this.getAuthToken();
        // console.log('JWT:', token);

        this.socket = io(socketUrl, {
          query: {
            token: token,
            source: 'ToolsPage',
          },
        });

        this.socket.on('connect', () => {
          // console.log('Socket successfully connected:', this.socket.id); // Log connection success
        });

        this.socket.on('connect_error', (error) => {
          console.error('Socket connection error:', error.message); // Log connection errors
        });

        this.socket.on('disconnect', (reason) => {
          console.warn(`Socket disconnected: ${reason}`); // Log disconnection events
        });

        this.socket.on('reconnect_attempt', () => {
          // console.info( `Socket attempting to reconnect: Attempt #${attemptNumber}`,); // Log reconnection attempts
        });

        this.socket.on('disconnect', (reason) => {
          try {
            if (
              reason === 'io server disconnect' ||
              reason === 'Authentication error'
            ) {
              this.output +=
                '\nDisconnected due to authentication error. Please log in again.';
            }
          } catch (error) {
            console.error('Error handling disconnect:', error);
          }
        });

        this.socket.on('connect_error', (error) => {
          try {
            console.error('Connection error:', error.message);
            if (error.message === 'Authentication error') {
              this.output += '\nAuthentication failed. Please log in again.';
            }
          } catch (error) {
            console.error('Error handling connect_error:', error);
          }
        });

        this.socket.on('scriptOutput', (data) => {
          try {
            let htmlContent = marked.parse(data);
            htmlContent = this.preprocessHighlight(htmlContent);
            htmlContent = htmlContent.replace('<a ', '<a target="_blank" ');
            this.output += htmlContent;
          } catch (error) {
            console.error('Error processing scriptOutput:', error);
          }
        });

        this.socket.on('scriptStopped', (message) => {
          try {
            let htmlContent = marked.parse(message);
            htmlContent = this.preprocessHighlight(htmlContent);
            this.output += htmlContent + '<br>';
          } catch (error) {
            console.error('Error processing scriptStopped:', error);
          }
        });
      } catch (error) {
        console.error('Error setting up socket connection:', error);
        this.output +=
          '\nAn unexpected error occurred while setting up the socket connection. Please try again later.';
      }
    },
  },
  mounted() {
    this.connectSocket();
  },

  beforeUnmount() {
    if (this.socket) {
      this.socket.disconnect();
    }
  },
};
</script>

<style scoped>
.page-title {
  font-weight: bold;
  margin-left: 15px;
  margin-bottom: 5px;
  width: 100vh;
}

button {
  height: 25px;
  margin-right: 4px;
  border-radius: 4px;
  width: 45px;
  border: 1pt solid #eee;
  background: #7354775c;
}

.tools-page {
  display: flex;
  justify-content: center;
  font-size: 0.9em;
}

.tools-container {
  display: flex;
  width: 100%; /* Full width container */
}

.tasks-column {
  flex: 0 0 250px; /* Fixed width for tasks column */
  padding: 10px;
  border-right: 1px solid #ccc; /* Optional: visual separation */
  max-width: 450px;
}

.task-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.task-name {
  margin-right: 10px;
  width: 275px;
  border: 1pt solid #888;
  border-radius: 4px;
  background: #efefef;
  padding: 4px;
}

.task-arg-input {
  margin-right: 10px;
  width: 111px;
  border: 1pt solid #888;
  border-radius: 4px;
  background: #efefef;
  padding: 4px;
}

.empty-space {
  width: 175px;
  margin-right: -5px;
}

.output-column {
  flex: 1; /* Take remaining space */
  padding: 10px;
  overflow: hidden;
}

.output-column.shrunk {
  width: 700px; /* Shrink output column when toolData is active */
}

.output-display {
  border: 1px solid #888;
  padding: 10px;
  background: #f5f5f5;
  height: 650px;
  max-width: 880px;
  overflow-y: auto;
  overflow-x: auto;
  border-radius: 4px;
  white-space: pre-wrap;
  word-wrap: normal;
  font-family: monospace;
  -webkit-user-select: text;
  user-select: text;
  cursor: text;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.output-display :deep(*) {
  -webkit-user-select: text;
  user-select: text;
  cursor: text;
}

.output-display::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.button-row {
  margin-bottom: 5px;
  margin-top: 0px;
}

.input-pane {
  display: flex;
  margin-top: 10px;
}

.input-pane input {
  flex: 1;
  padding: 10px;
  margin-right: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-width: 700px;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: red;
  color: white;
  border: none;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 5px;
}

:deep(p) {
  margin: 2px !important;
  display: contents !important;
}

:deep(.highlight-term) {
  background: #d6bbda;
}

:deep(ul) {
  margin: 2px !important;
  display: inline-flex;
}
:deep(li) {
  margin: 2px !important;
}
:deep(h3) {
  margin: 2px !important;
  display: inline-flex;
}
</style>
